import type { ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

import {
    gsap,
    MOTION,
    EASE,
    registerGsap,
    prefersReducedMotion,
    useIsomorphicLayoutEffect
} from '@/lib/motion'

type RevealProps = {
    children: ReactNode
    className?: string
    /** Play on mount instead of waiting for the element to scroll into view. */
    immediate?: boolean
    delay?: number
    duration?: number
    y?: number
    blur?: number
    /** Stagger the element's direct children instead of the element itself. */
    stagger?: number | false
    start?: string
}

/**
 * The single entrance primitive used across the site: opacity in, a short lift
 * upward and a blur that resolves. Passing `stagger` animates the element's
 * direct children in sequence instead of the wrapper.
 */
export const Reveal = ({
    children,
    className,
    immediate = false,
    delay = 0,
    duration = MOTION.duration,
    y = MOTION.distance,
    blur = MOTION.blur,
    stagger = false,
    start = MOTION.start
}: RevealProps) => {
    const ref = useRef<HTMLDivElement | null>(null)

    useIsomorphicLayoutEffect(() => {
        const node = ref.current

        if (!node || prefersReducedMotion()) {
            return
        }

        registerGsap()

        const targets = stagger === false ? [node] : Array.from(node.children)

        if (!targets.length) {
            return
        }

        const context = gsap.context(() => {
            gsap.set(targets, {
                opacity: 0,
                y,
                filter: blur ? `blur(${blur}px)` : 'blur(0px)',
                willChange: 'transform, opacity, filter'
            })

            gsap.to(targets, {
                opacity: 1,
                y: 0,
                filter: 'blur(0px)',
                duration,
                delay,
                ease: EASE.enter,
                stagger: stagger === false ? 0 : stagger,
                clearProps: 'filter,willChange',
                scrollTrigger: immediate
                    ? undefined
                    : {
                        trigger: node,
                        start,
                        once: true
                    }
            })
        }, node)

        return () => context.revert()
    }, [immediate, delay, duration, y, blur, stagger, start])

    return (
        <div
            ref={ref}
            data-reveal=''
            className={cn(className)}
        >
            {children}
        </div>
    )
}
